import React, {useCallback, useEffect, useMemo} from 'react';
import {FlatList, Platform, View} from 'react-native';
import {useNavigation, useRoute} from '@react-navigation/native';

import {setIsViewTeam} from '/redux/action/myTeams.action';

import {useMyTeamsBusiness} from './hooks/useMyTeamsBusiness';
import {useMyTeamsContext} from './hooks/useMyTeamsContext';
import CoachRowItem from './CoachRowItem';
import {CoachInformation} from './TeamOrgReport';
import {styles} from './TeamOrgReportList.style';
import WatchlistEmptyState from './WatchlistEmptyState';

interface ReportMeta {
  page: {
    total: number;
  };
}

interface WatchlistReportListProps {
  onPress: (coach: CoachInformation) => void;
  item: CoachInformation[];
  downlineCoachIds: string[];
  count: number;
  lastPaidRank: string;
  highestAchievedRank: string;
  coachCurrentRank: string;
  reportFilterCount: number;
  previousScreenName: string;
  currentScreenName: string;
  userId: string;
  reportMeta?: ReportMeta | null;
}

function WatchlistReportList({
  onPress,
  item = [],
  downlineCoachIds = [],
  count,
  lastPaidRank,
  highestAchievedRank,
  coachCurrentRank,
  reportFilterCount,
  previousScreenName,
  currentScreenName,
  userId,
  reportMeta,
}: WatchlistReportListProps) {
  const navigation = useNavigation();
  const route = useRoute();
  const {reduxActiveTab, dispatch} = useMyTeamsContext();
  const {
    viewTeamLoadingId,
    loadingStarId,
    handleViewTeamPress,
    toggleAddWatchlist,
    formatNumber,
  } = useMyTeamsBusiness({
    navigation,
    route,
    isViewTeam: false,
    downlineCoachIds,
    lastPaidRank,
    highestAchievedRank,
    coachCurrentRank,
    count,
    previousScreenName,
    currentScreenName,
    userId,
    reportFilterCount,
    isWatchlistScreen: true,
    tabsValue: reduxActiveTab,
  });

  //If the tab is 'watchlist', then update the screen's parameters to set isViewTeam to false
  useEffect(() => {
    if (reduxActiveTab === 'watchlist') {
      dispatch(setIsViewTeam(false));
    }
  }, [reduxActiveTab, dispatch]);

  const filteredData = useMemo(() => {
    if (!Array.isArray(item) || !downlineCoachIds.length) return [];
    return item.filter(coach => downlineCoachIds.includes(String(coach.id)));
  }, [item, downlineCoachIds]);

  const renderCoach = useCallback(
    ({item: coach, index}: {item: CoachInformation; index: number}) => {
      const accessibilityData = {
        index,
        totalCount: count || filteredData.length,
        reportMeta,
      };

      return (
        <View
          key={coach.id}
          testID={`coach-${coach.id}`}
          accessible={false}
          collapsable={false}>
          <CoachRowItem
            item={coach}
            formatNumber={formatNumber}
            isInWishlist={true}
            onToggleWishlist={() => toggleAddWatchlist(coach)}
            indent={0}
            showChevron={false}
            isExpanded={false}
            onToggleExpand={() => {}}
            level={0}
            onPress={onPress}
            onViewTeamPress={handleViewTeamPress}
            viewTeamLoading={viewTeamLoadingId === coach.id}
            showViewTeamButton={coach.isViewTeam === true}
            iswatchList={true}
            starLoading={loadingStarId === coach.id}
            tabsValue={reduxActiveTab}
            accessibilityData={accessibilityData}
            wishlistTestID={`wishlist-${coach.id}`}
            expandTestID={`expand-${coach.id}`}
          />

          <View style={styles.nodeSeparator} />
        </View>
      );
    },
    [
      formatNumber,
      toggleAddWatchlist,
      handleViewTeamPress,
      viewTeamLoadingId,
      loadingStarId,
      reduxActiveTab,
      onPress,
      count,
      filteredData.length,
      reportMeta,
    ],
  );

  const keyExtractor = useCallback(
    (item: CoachInformation) => String(item.id),
    [],
  );

  if (!downlineCoachIds.length) {
    return <WatchlistEmptyState />;
  }

  return (
    <FlatList
      contentContainerStyle={styles.container}
      data={filteredData}
      renderItem={renderCoach}
      keyExtractor={keyExtractor}
      removeClippedSubviews={true}
      maxToRenderPerBatch={10}
      windowSize={10}
      ListFooterComponent={
        Platform.OS === 'web' ? <View style={{height: 140}} /> : null
      }
    />
  );
}

WatchlistReportList.defaultProps = {
  reportMeta: null,
};

export default React.memo(WatchlistReportList);
